import { Typography, Card, ChevronRightIcon, useToast, LoadingToast } from "@fanka/ui-admin";
import { MedicalMenusPageProps } from "@on-site/components/pages/directVisit/ui/medicalMenus/type";
import { useLocalViewsStore } from "@on-site/features/top/hooks/useLocalViewsStore";
import { GoBackViewButton } from "@on-site/features/top/ui/GoBackViewButton";

import { useClinicParams } from "@on-site/hooks/useClinicParams";
import { PageLayout } from "@on-site/layouts/PageLayout";

import { useAppApiSWR } from "@on-site/libs/swr/useAppApiSWR";
import { useAppApiSWRPost } from "@on-site/libs/swr/useAppApiSWRMutation";
import { parseToString } from "@on-site/utils/parse";

export const MedicalMenusPage = ({ department, patientNo, onGoBack, onRequestSuccess }: MedicalMenusPageProps) => {
  const { setLocalView } = useLocalViewsStore();
  const { clinicId } = useClinicParams();

  const { openToast } = useToast();

  const { data: departmentsData } = useAppApiSWR("/api/v2/public/clinics/{clinicId}/departments/direct-visit", {
    params: {
      path: {
        clinicId,
      },
    },
  });

  const { data: medicalMenusData, isLoading } = useAppApiSWR(
    "/api/v2/public/clinics/{clinicId}/medical-menus/direct-visit",
    {
      params: {
        path: {
          clinicId,
        },
        query: {
          departmentId: parseToString(department.id, "dummy"),
        },
      },
      onSuccess: async ({ medicalMenus }) => {
        // NOTE: 診療メニューが一つの場合は選択をスキップ
        if (!!medicalMenus && medicalMenus.length === 1 && !isMutating) {
          const medicalMenu = medicalMenus[0];
          await trigger({ medicalMenuId: medicalMenu.id, externalPatientReference: patientNo });
          onRequestSuccess();
        }
      },
      onError: (error) => {
        openToast({ type: "error", description: error?.message });
      },
    },
  );

  const { trigger, isMutating } = useAppApiSWRPost("/api/v2/on-site/clinics/{clinicId}/events/direct-visit", {
    params: {
      path: {
        clinicId,
      },
    },
    onSuccess: onRequestSuccess,
    onError: (error) => {
      openToast({ type: "error", description: error?.message });
    },
  });

  const handleGoBack = () => {
    // NOTE: 診療科が一つの場合は選択がスキップされているのでデフォルトViewに戻る
    if (departmentsData?.departments?.length === 1) {
      return setLocalView("DEFAULT");
    }

    return onGoBack();
  };

  if (isLoading || medicalMenusData?.medicalMenus.length === 1) return <LoadingToast isLoading />;

  return (
    <PageLayout leftTopButton={<GoBackViewButton onClick={handleGoBack} />}>
      <div className="mx-auto flex h-full w-full max-w-[928px] flex-col items-center justify-center gap-y-6 px-6">
        <section className="flex flex-col gap-y-12">
          <div className="flex flex-col items-center gap-y-6">
            <div className="bg-grayScale-500 text-ui-white w-fit rounded-[5px] px-2.5 py-1">
              <Typography.UiText size="lg" bold>
                当日受付でのご案内
              </Typography.UiText>
            </div>
            <Typography.Heading type="h1">診察メニューをお選びください</Typography.Heading>
          </div>

          <div className="flex flex-col items-center justify-center gap-y-2">
            <div className="text-grayScale-400">
              <Typography.UiText size="md" bold>
                希望診療内容
              </Typography.UiText>
            </div>
            <Typography.Heading type="h2">{department.name}</Typography.Heading>
          </div>
        </section>
        <div className="flex flex-wrap items-center justify-center gap-4">
          {medicalMenusData?.medicalMenus?.map(({ id, displayName }) => (
            <section className="w-[282px]">
              <Card onClick={() => !isMutating && trigger({ medicalMenuId: id, externalPatientReference: patientNo })}>
                <div className="flex items-center justify-between">
                  <Typography.UiText size="lg" bold>
                    {displayName}
                  </Typography.UiText>
                  <ChevronRightIcon height={24} width={24} className="fill-grayScale-300" />
                </div>
              </Card>
            </section>
          ))}
        </div>
      </div>
    </PageLayout>
  );
};
